import { useModals, Modal, ModalProps, Button, Input, Stack } from "@skailer-com/uikit";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { CustomFieldOptionPayload } from "@/types/custom-field.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface CustomFieldOptionEditModalProps extends ModalProps {
  option?: CustomFieldOptionPayload | undefined;
  onUpdate?: (payload: CustomFieldOptionPayload, isNew: boolean) => void;
}

export function CustomFieldOptionEditModal({
  onUpdate,
  option,
  ...props
}: CustomFieldOptionEditModalProps) {
  const { id } = props;
  const { t } = useTranslation();
  const { close } = useModals();
  const CustomFieldOptionSchemaObject = z.object({
    title: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
    description: z.string().optional(),
    value_text: z.string().optional(),
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(CustomFieldOptionSchemaObject),
    defaultValues: {
      title: option?.title ?? "",
      description: option?.description ?? "",
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    onUpdate?.(
      {
        title: data.title,
        description: data.description ?? "",
        id: option?.id,
      },
      !option
    );

    if (option) {
      analyticsService.trackEvent(analyticEvents.customFields.optionEdited);
    } else {
      analyticsService.trackEvent(analyticEvents.customFields.optionCreated);
    }

    close(id);
  });

  return (
    <Modal
      {...props}
      withCloser={true}
      title={option ? t("custom_fields:edit_option") : t("common:add_option")}
      actions={
        <Stack gap={"md"}>
          <Button size={"lg"} variant={"primary"} onClick={onSubmit}>
            {option ? t("common:save") : t("common:create")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          required
          autoFocus
          label={t("common:title")}
          {...register("title")}
          error={errors.title?.message}
          size={"lg"}
        />
        <Input
          label={t("common:description")}
          {...register("description")}
          error={errors.description?.message}
        />
      </form>
    </Modal>
  );
}
